import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { experiences } from '../data';
import { experiencePriceLabel } from '../quote/experiencePriceLabel';
import CategoryTabs from '../components/CategoryTabs';
import PackageBuilderPreview from '../components/PackageBuilderPreview';
import './HomePage.css';
import heroMountains from '../images/teamward-mountains.png';
import heroWater from '../images/teamward-water.png';
import heroCelebration from '../images/teamward-celebration.jpg';

const PRICE_RANK = {
  $: 1,
  $$: 2,
  $$$: 3,
};

const normalizeValue = (value) =>
  String(value || '').trim().toLowerCase();

const parseGroupRange = (value) => {
  const text = String(value || '');
  const numbers = text.match(/\d+/g)?.map(Number) || [];

  if (text.includes('+') && numbers.length > 0) {
    return [numbers[0], Infinity];
  }

  if (numbers.length >= 2) {
    return [numbers[0], numbers[1]];
  }

  if (numbers.length === 1) {
    return [numbers[0], numbers[0]];
  }

  return [0, Infinity];
};

const rangesOverlap = (
  [minimumA, maximumA],
  [minimumB, maximumB]
) => minimumA <= maximumB && minimumB <= maximumA;

const getExperienceCategories = (experience) => {
  const categories =
    experience.primaryCategories ??
    (experience.primaryCategory
      ? [experience.primaryCategory]
      : ['outings']);

  return (Array.isArray(categories)
    ? categories
    : [categories]
  ).map(normalizeValue);
};

const getExperienceVibes = (experience) => {
  const vibes = Array.isArray(experience.vibe)
    ? experience.vibe
    : [experience.vibe];

  return vibes.filter(Boolean).map(normalizeValue);
};

const getExperienceSettings = (experience) => {
  const settings = Array.isArray(experience.setting)
    ? experience.setting
    : [experience.setting];

  return settings.filter(Boolean);
};

const plannerDefaults = {
  city: 'Denver',
  groupSize: '11–25',
  budget: '$$',
  setting: 'Any',
  vibe: 'Any',
};

const categoryFilterConfig = {
  outings: {
    label: 'Vibe',
    options: [
      'Any',
      'Active',
      'Creative',
      'Competitive',
      'Social',
      'Relaxed',
      'Purpose-driven',
    ],
  },
  trainings: {
    label: 'Training Focus',
    options: [
      'Any',
      'Leadership',
      'Communication',
      'Team Building',
      'Professional Development',
      'Sales',
      'Workplace Wellness',
    ],
  },
  speakers: {
    label: 'Speaking Topic',
    options: [
      'Any',
      'Leadership',
      'Motivation',
      'Communication',
      'Workplace Culture',
      'Wellness',
      'Innovation',
    ],
  },
  retreats: {
    label: 'Retreat Goal',
    options: [
      'Any',
      'Team Bonding',
      'Strategic Planning',
      'Leadership Development',
      'Wellness',
      'Celebration',
      'Rest and Recharge',
    ],
  },
  volunteer: {
    label: 'Cause Area',
    options: [
      'Any',
      'Community',
      'Hunger Relief',
      'Environment',
      'Youth',
      'Animals',
      'Housing',
    ],
  },
};

const HomePage = () => {
  const [planner, setPlanner] = useState(plannerDefaults);
  const [activeCategory, setActiveCategory] = useState('outings');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const categoryFilter = categoryFilterConfig[activeCategory];

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setPlanner((current) => ({
      ...current,
      vibe: 'Any',
      budget: categoryId === 'outings' ? '$$' : '$$$',
    }));
  };

  const plannerResults = useMemo(() => {
    const selectedGroupRange = parseGroupRange(planner.groupSize);
    const selectedBudgetRank = PRICE_RANK[planner.budget] || Infinity;

    const rankedExperiences = experiences.map((experience) => {
      const experienceCategories = getExperienceCategories(experience);
      const experienceVibes = getExperienceVibes(experience);
      const experienceSettings = getExperienceSettings(experience);

      const categoryMatch = experienceCategories.includes(
        normalizeValue(activeCategory)
      );
      const cityMatch =
        normalizeValue(experience.city) === normalizeValue(planner.city);
      const groupMatch = rangesOverlap(
        parseGroupRange(experience.groupSize),
        selectedGroupRange
      );
      const experienceBudgetRank = PRICE_RANK[experience.price] || Infinity;
      const budgetMatch = experienceBudgetRank <= selectedBudgetRank;
      const settingMatch =
        planner.setting === 'Any' ||
        experienceSettings.some((setting) =>
          normalizeValue(setting).includes(normalizeValue(planner.setting))
        );
      const vibeMatch =
        planner.vibe === 'Any' ||
        experienceVibes.includes(normalizeValue(planner.vibe));

      const differences = [];
      if (!cityMatch) {
        differences.push(
          `Different city: ${experience.city}, ${experience.state}`
        );
      }
      if (!groupMatch) {
        differences.push(`Group-size range: ${experience.groupSize} people`);
      }
      if (!budgetMatch) {
        differences.push(`Above selected budget: ${experiencePriceLabel(experience)}`);
      }
      if (!settingMatch) {
        differences.push(`Different setting: ${experienceSettings.join(', ')}`);
      }
      if (!vibeMatch) {
        const displayedVibes = Array.isArray(experience.vibe)
          ? experience.vibe.join(', ')
          : experience.vibe;
        differences.push(
          `Different ${categoryFilter.label.toLowerCase()}: ${
            displayedVibes || 'not specified'
          }`
        );
      }
      if (!categoryMatch) {
        differences.push(`Different experience type: ${experience.category}`);
      }

      const isExactMatch =
        categoryMatch &&
        cityMatch &&
        groupMatch &&
        budgetMatch &&
        settingMatch &&
        vibeMatch;

      let score = 0;
      if (categoryMatch) score += 100;
      if (cityMatch) score += 40;
      if (groupMatch) score += 35;
      if (budgetMatch) score += 20;
      if (settingMatch) score += 10;
      if (vibeMatch) score += 8;
      if (experience.price === planner.budget) score += 3;

      return {
        experience,
        differences,
        isExactMatch,
        categoryMatch,
        score,
      };
    });

    const sortByCloseness = (first, second) =>
      second.score - first.score ||
      first.differences.length - second.differences.length;

    const exactMatches = rankedExperiences
      .filter((item) => item.isExactMatch)
      .sort(sortByCloseness)
      .slice(0, 3);

    const sameCategoryAlternatives = rankedExperiences
      .filter((item) => !item.isExactMatch && item.categoryMatch)
      .sort(sortByCloseness);

    const otherCategoryAlternatives = rankedExperiences
      .filter((item) => !item.isExactMatch && !item.categoryMatch)
      .sort(sortByCloseness);

    return {
      exactMatches,
      closestMatches: [
        ...sameCategoryAlternatives,
        ...otherCategoryAlternatives,
      ].slice(0, 3),
    };
  }, [activeCategory, categoryFilter.label, planner]);

  const hasExactMatches = plannerResults.exactMatches.length > 0;
  const displayedMatches = hasExactMatches
    ? plannerResults.exactMatches
    : plannerResults.closestMatches;

  const updatePlanner = (field, value) => {
    setPlanner((current) => ({ ...current, [field]: value }));
  };

  const resetPlanner = () => {
    setActiveCategory('outings');
    setPlanner(plannerDefaults);
  };

  const scrollToPlanner = () => {
    document.getElementById('planner')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <main className="teamward-home">
      <section className="tw-showcase-hero">
        <div className="tw-showcase-copy">
          <p className="tw-showcase-eyebrow">
            Better experiences. Stronger teams.
          </p>

          <h1>
            Outdo the Conference Room
            <sup className="registered-mark">®</sup>
          </h1>

          <p className="tw-showcase-description">
            Teamward helps busy managers, HR teams, and executive
            assistants find the right team experience in minutes —
            not hours.
          </p>

          <p className="tw-showcase-description-mobile">
            Find the right team experience in minutes—not hours.
          </p>

          <div className="tw-showcase-actions">
            <button
              type="button"
              className="tw-showcase-primary"
              onClick={scrollToPlanner}
            >
              Find the Right Experience
            </button>

            <div className="tw-showcase-builder-action">
              <Link
                to="/quote-builder"
                className="tw-showcase-secondary"
                aria-describedby="tw-showcase-builder-availability"
              >
                Build a Package
              </Link>
              <span
                id="tw-showcase-builder-availability"
                className="tw-showcase-builder-availability"
              >
                Denver self-serve builder
              </span>
            </div>

            <Link
              to="/planning-assistance"
              className="tw-showcase-secondary"
            >
              Get Planning Help
            </Link>
          </div>

          <div className="tw-showcase-award">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M7 4h10v4a5 5 0 0 1-10 0V4Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 6H4v1a4 4 0 0 0 4 4M17 6h3v1a4 4 0 0 1-4 4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 13v5M9 21h6M10 18h4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
            <span>2023 Casper Start-Up Challenge Winner</span>
          </div>
        </div>

        <div className="tw-showcase-visual">
          <img
            className="tw-showcase-mobile-photo"
            src={heroMountains}
            alt="A team enjoying an outdoor experience together"
          />

          <svg
            className="tw-collage-svg"
            viewBox="0 0 900 760"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <clipPath id="tw-top-photo">
                <polygon points="30,0 668,0 900,330 246,371" />
              </clipPath>
              <clipPath id="tw-left-photo">
                <polygon points="246,371 514,760 7,760" />
              </clipPath>
              <clipPath id="tw-right-photo">
                <polygon points="246,371 900,330 900,760 514,760" />
              </clipPath>
            </defs>

            <image
              href={heroMountains}
              x="20"
              y="-5"
              width="900"
              height="430"
              preserveAspectRatio="xMidYMid slice"
              clipPath="url(#tw-top-photo)"
            />
            <image
              href={heroWater}
              x="-20"
              y="345"
              width="570"
              height="430"
              preserveAspectRatio="xMidYMid slice"
              clipPath="url(#tw-left-photo)"
            />
            <image
              href={heroCelebration}
              x="170"
              y="325"
              width="780"
              height="540"
              preserveAspectRatio="xMidYMid slice"
              clipPath="url(#tw-right-photo)"
            />

            <path d="M30 0 L246 371" fill="none" stroke="#ffffff" strokeWidth="11" />
            <path d="M668 0 L900 330" fill="none" stroke="#ffffff" strokeWidth="11" />
            <path d="M246 371 L900 330" fill="none" stroke="#ffffff" strokeWidth="11" />
            <path d="M7 760 L246 371" fill="none" stroke="#ffffff" strokeWidth="11" />
            <path d="M246 371 L514 760" fill="none" stroke="#ffffff" strokeWidth="11" />
          </svg>

          <div className="tw-launch-card">
            <span>Experience matching</span>
            <strong>Casper + Denver</strong>
            <p>
              Self-serve package builder available for Denver events.
            </p>
          </div>
        </div>
      </section>

      <section className="teamward-how-compact">
        <div className="teamward-how-compact-inner">
          <div className="teamward-how-compact-title">
            <p>How Teamward works</p>
          </div>

          <div className="teamward-how-compact-steps">
            <div className="teamward-how-compact-step">
              <span className="teamward-how-number">01</span>
              <div>
                <h3>Tell us about your team</h3>
                <p>City, group size, budget, and preferences.</p>
              </div>
            </div>

            <div className="teamward-how-arrow" aria-hidden="true">→</div>

            <div className="teamward-how-compact-step">
              <span className="teamward-how-number">02</span>
              <div>
                <h3>See your strongest matches</h3>
                <p>Curated experiences that fit your selections.</p>
              </div>
            </div>

            <div className="teamward-how-arrow" aria-hidden="true">→</div>

            <div className="teamward-how-compact-step">
              <span className="teamward-how-number">03</span>
              <div>
                <h3>Take the next step your way</h3>
                <p>
                  Build your package and see the cost, or get planning help.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="planner" className="finder-hero">
        <div className="finder-hero-heading">
          <p className="finder-eyebrow">Team experience planning</p>
          <h1>Find the right experience for your team.</h1>
          <p className="finder-subheading">
            Choose a few details and see your strongest matches.
          </p>
        </div>

        <div className="finder-shell">
          <CategoryTabs
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />

          <div className="finder-grid">
            <aside className="finder-filters" aria-label="Experience filters">
              <div className="planner-field">
                <label htmlFor="hero-planner-city">City</label>
                <select
                  id="hero-planner-city"
                  value={planner.city}
                  onChange={(event) =>
                    updatePlanner('city', event.target.value)
                  }
                >
                  <option value="Denver">Denver, CO</option>
                  <option value="Casper">Casper, WY</option>
                </select>
              </div>

              <div className="planner-field">
                <label htmlFor="hero-planner-group-size">Group size</label>
                <select
                  id="hero-planner-group-size"
                  value={planner.groupSize}
                  onChange={(event) =>
                    updatePlanner('groupSize', event.target.value)
                  }
                >
                  <option>5–10</option>
                  <option>11–25</option>
                  <option>26–50</option>
                  <option>50+</option>
                </select>
              </div>

              <div className="planner-field">
                <label htmlFor="hero-planner-budget">Budget</label>
                <select
                  id="hero-planner-budget"
                  value={planner.budget}
                  onChange={(event) =>
                    updatePlanner('budget', event.target.value)
                  }
                >
                  <option>$</option>
                  <option>$$</option>
                  <option>$$$</option>
                </select>
              </div>

              <div className="planner-field">
                <label htmlFor="hero-planner-setting">Setting</label>
                <select
                  id="hero-planner-setting"
                  value={planner.setting}
                  onChange={(event) =>
                    updatePlanner('setting', event.target.value)
                  }
                >
                  <option>Any</option>
                  <option>Indoor</option>
                  <option>Outdoor</option>
                  <option>Virtual</option>
                </select>
              </div>

              <div className="planner-field">
                <label htmlFor="hero-planner-category-filter">
                  {categoryFilter.label}
                </label>
                <select
                  id="hero-planner-category-filter"
                  value={planner.vibe}
                  onChange={(event) =>
                    updatePlanner('vibe', event.target.value)
                  }
                >
                  {categoryFilter.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="finder-clear-button"
                onClick={resetPlanner}
              >
                <span aria-hidden="true">↻</span>
                Clear filters
              </button>
            </aside>

            <section className="finder-results" aria-live="polite">
              <div className="finder-results-heading">
                <div>
                  <p className="finder-results-kicker">
                    Curated for your selections
                  </p>
                  <h2>{hasExactMatches ? 'Top matches' : 'Closest matches'}</h2>
                </div>

                <Link to="/browse" className="finder-view-all">
                  View all matches <span aria-hidden="true">→</span>
                </Link>
              </div>

              {!hasExactMatches && (
                <div className="finder-no-exact">
                  <strong>No exact matches for every filter.</strong>
                  <span>These are the closest available options.</span>
                </div>
              )}

              <div className="finder-match-list">
                {displayedMatches.slice(0, 3).map(({ experience }) => (
                  <Link
                    to={`/business/${experience.id}`}
                    className="finder-match"
                    key={experience.id}
                  >
                    <img src={experience.image} alt={experience.name} />

                    <div className="finder-match-copy">
                      <strong>{experience.name}</strong>
                      <span className="finder-match-details">
                        {experience.city}, {experience.state} ·{' '}
                        {experiencePriceLabel(experience)} ·{' '}
                        {experience.duration}
                      </span>
                      <p>{experience.summary}</p>
                    </div>

                    <span className="finder-match-action">View Details</span>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </div>

        <div
          className="finder-award-row"
          aria-label="2023 Casper Start-Up Challenge Winner"
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M7 4h10v4a5 5 0 0 1-10 0V4Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7 6H4v1a4 4 0 0 0 4 4M17 6h3v1a4 4 0 0 1-4 4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 13v5M9 21h6M10 18h4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          <span>2023 Casper Start-Up Challenge Winner</span>
        </div>
      </section>

      <PackageBuilderPreview />
    </main>
  );
};

export default HomePage;
